import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { TicketCategoriesModel } from '../../../../shared/models/ticket.model';
import { getTicketCategories } from '../../../../shared/services/data/ticket.data';
import {
  CategoryOption,
  categoryGroups,
  emergencyChoice
} from '../../../../shared/services/helpers/ticket-rules.helper';

interface Props {
  saving: boolean;
  onSubmit: (payload: { code: string; emergency?: boolean }) => void;
  /** Change the type of a ticket that has one (CTO 2026-10-14), instead of setting the first. */
  change?: { code: string; team: string; onCancel: () => void };
}

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

/**
 * A ticket that came in by email has no category, so it has no owner chain, no
 * default priority and no handoff. This is where the team gives it one. That can
 * only happen once, and it routes the ticket to the category's first team.
 *
 * With `change`, the same picker re-types a ticket that already has a category (the team
 * re-tags, e.g. Inquiry to Request). The ticket stays with its team, its priority never
 * drops, and the thread records who changed it from what.
 */
export const CategorizeBanner: React.FC<Props> = ({ saving, onSubmit, change }) => {
  const [doc, setDoc] = useState<TicketCategoriesModel | undefined>();
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [emergency, setEmergency] = useState(false);

  useEffect(() => {
    getTicketCategories().then((result) => {
      if (!isErrorInfo(result)) setDoc(result);
    });
  }, []);

  const current = change?.code;
  const groups = useMemo(() => {
    const all = doc ? categoryGroups(doc) : [];
    // the current type is not a change
    return current
      ? all.map((g) => ({ ...g, options: g.options.filter((o) => o.value !== current) }))
      : all;
  }, [doc, current]);
  const rule = category ? emergencyChoice(category.item.emergency) : undefined;
  const firstTeam = category?.item.ownerChain[0]?.teams[0];

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-creams-600 bg-creams-100 p-4">
      {change ? (
        <div className="text-sm text-neutral-500">
          <span className="font-gilroy-bold">Change the ticket type</span> (now {change.code}). It
          stays with {change.team}; the priority can only go up, and the thread records the change.
        </div>
      ) : (
        <div className="text-sm text-neutral-500">
          <span className="font-gilroy-bold">
            This ticket came in by email and has no category.
          </span>{' '}
          Pick one: it decides the team, the default priority and who it can be handed off to.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[280px] flex-1">
          <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            isLoading={!doc}
            options={groups}
            placeholder="Category"
            value={category}
            onChange={(option: ValueType<CategoryOption, false>) => {
              const next = (option as CategoryOption) || null;
              setCategory(next);
              setEmergency(next ? emergencyChoice(next.item.emergency).value : false);
            }}
          />
        </div>
        {rule ? (
          <label className="flex items-center gap-2 text-sm text-neutral-400">
            <input
              type="checkbox"
              checked={emergency}
              disabled={!rule.editable}
              onChange={(event) => setEmergency(event.target.checked)}
            />
            Emergency
          </label>
        ) : null}
        <Button
          type={ButtonType.primary}
          disabled={!category || saving}
          loading={saving}
          onClick={() =>
            category &&
            onSubmit({ code: category.value, emergency: rule?.editable ? emergency : undefined })
          }
        >
          {change ? 'Change type' : 'Set category'}
        </Button>
        {change ? (
          <Button type={ButtonType.gray} disabled={saving} onClick={change.onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
      {firstTeam && !change ? (
        <p className="text-xs text-neutral-400">Goes to {firstTeam}.</p>
      ) : null}
      {category && change ? (
        <p className="text-xs text-neutral-400">
          Default priority {category.item.priority}; handoffs follow{' '}
          {category.item.ownerChain.map((s) => s.teams.join(' / ')).join(' → ')}.
        </p>
      ) : null}
    </section>
  );
};
